import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ResourceCard from './components/ResourceCard';
import FilterPanel from './components/FilterPanel';
import FeaturedSection from './components/FeaturedSection';
import WebinarSection from './components/WebinarSection';
import NewsletterSection from './components/NewsletterSection';
import InteractiveToolsSection from './components/InteractiveToolsSection';

const ResourcesHub = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    type: [],
    role: [],
    industry: [],
    pillar: []
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');

  // Mock resources data
  const allResources = [
    {
      id: 1,
      title: 'Enterprise AI Strategy Framework 2025',
      description: 'Comprehensive framework for developing and implementing AI strategies that drive competitive advantage while maintaining organizational independence.',
      type: 'whitepaper',
      targetRole: 'C-Suite',
      industry: 'Technology',
      pillar: 'AI Survival Strategy',
      readTime: '25 min read',
      downloads: '3.2K',
      publishDate: 'Jan 2025',
      featured: false
    },
    {
      id: 2,
      title: 'Open-Source AI Security Best Practices',
      description: 'Technical guide covering security considerations, risk mitigation strategies, and compliance frameworks for enterprise open-source AI implementations.',
      type: 'guide',
      targetRole: 'Technical',
      industry: 'Financial Services',
      pillar: 'Open-Source Superiority',
      readTime: '40 min read',
      downloads: '2.1K',
      publishDate: 'Dec 2024',
      featured: false
    },
    {
      id: 3,
      title: 'Healthcare AI Transformation Case Study',
      description: 'Detailed analysis of how a major healthcare system achieved 60% cost reduction and improved patient outcomes through open-source AI adoption.',
      type: 'video',
      targetRole: 'Business',
      industry: 'Healthcare',
      pillar: 'Internal Capability Building',
      readTime: '35 min watch',
      downloads: '1.8K',
      publishDate: 'Dec 2024',
      featured: false
    },
    {
      id: 4,
      title: 'Vendor Dependency Risk Assessment Tool',
      description: 'Interactive assessment to evaluate your organization\'s exposure to vendor lock-in risks and identify mitigation strategies.',
      type: 'assessment',
      targetRole: 'Procurement',
      industry: 'Manufacturing',
      pillar: 'Vendor Lock-in Risks',
      readTime: '15 min tool',
      downloads: '4.7K',
      publishDate: 'Jan 2025',
      featured: false
    },
    {
      id: 5,
      title: 'Building Internal AI Teams: Recruitment Guide',
      description: 'Practical guide for hiring, training, and retaining AI talent to build sustainable internal capabilities.',
      type: 'guide',
      targetRole: 'C-Suite',
      industry: 'Technology',
      pillar: 'Internal Capability Building',
      readTime: '30 min read',
      downloads: '2.9K',
      publishDate: 'Dec 2024',
      featured: false
    },
    {
      id: 6,
      title: 'AI ROI Measurement Framework',
      description: 'Comprehensive framework for measuring and reporting ROI from AI initiatives, including metrics, KPIs, and reporting templates.',
      type: 'whitepaper',
      targetRole: 'Business',
      industry: 'Retail',
      pillar: 'AI Survival Strategy',
      readTime: '20 min read',
      downloads: '3.5K',
      publishDate: 'Jan 2025',
      featured: false
    }
  ];

  const [filteredResources, setFilteredResources] = useState(allResources);

  // Filter and search logic
  useEffect(() => {
    let filtered = allResources;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(resource =>
        resource?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        resource?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        resource?.pillar?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply category filters
    Object.keys(filters)?.forEach(category => {
      if (filters?.[category]?.length > 0) {
        filtered = filtered?.filter(resource => {
          if (category === 'type') return filters?.[category]?.includes(resource?.type);
          if (category === 'role') return filters?.[category]?.includes(resource?.targetRole);
          if (category === 'industry') return filters?.[category]?.includes(resource?.industry);
          if (category === 'pillar') return filters?.[category]?.includes(resource?.pillar);
          return true;
        });
      }
    });

    // Apply sorting
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.publishDate) - new Date(a.publishDate);
        case 'popular':
          return parseInt(b?.downloads?.replace('K', '000')?.replace('.', '')) - 
                 parseInt(a?.downloads?.replace('K', '000')?.replace('.', ''));
        case 'title':
          return a?.title?.localeCompare(b?.title);
        default:
          return 0;
      }
    });

    setFilteredResources(filtered);
  }, [searchQuery, filters, sortBy]);

  const handleFilterChange = (category, values) => {
    setFilters(prev => ({
      ...prev,
      [category]: values
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      type: [],
      role: [],
      industry: [],
      pillar: []
    });
    setSearchQuery('');
  };

  const handleResourceDownload = (resource) => {
    console.log('Downloading resource:', resource?.title);
    // In a real app, this would trigger the download
  };

  const handleResourceView = (resource) => {
    console.log('Viewing resource:', resource?.title);
    // In a real app, this would open the resource viewer
  };

  const handleToolLaunch = (tool) => {
    console.log('Launching tool:', tool?.title);
    // In a real app, this would launch the interactive tool
  };

  const handleResourceClick = (resource) => {
    console.log('Clicking resource:', resource?.title);
    // In a real app, this would handle the resource click action
  };

  return (
    <>
      <Helmet>
        <title>Resources Hub - AI Clean Core | Enterprise AI Independence Resources</title>
        <meta name="description" content="Access comprehensive resources for achieving AI independence including whitepapers, calculators, webinars, and interactive tools for enterprise open-source AI adoption." />
        <meta name="keywords" content="AI resources, open source AI, enterprise AI, vendor lock-in, AI independence, whitepapers, calculators" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-20">
          {/* Hero Section */}
          <section className="bg-gradient-to-br from-brand-primary via-brand-primary to-brand-secondary text-white py-16 lg:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center max-w-4xl mx-auto">
                <h1 className="text-4xl lg:text-6xl font-bold mb-6">
                  AI Independence
                  <span className="block text-brand-accent">Resource Center</span>
                </h1>
                <p className="text-xl lg:text-2xl text-white/90 mb-8">
                  Your definitive source for strategic insights, technical guides, and practical tools to achieve AI independence and avoid vendor lock-in
                </p>
                
                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 mt-12">
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold text-brand-accent mb-2">50+</div>
                    <div className="text-white/80">Expert Resources</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold text-brand-accent mb-2">25K+</div>
                    <div className="text-white/80">Downloads</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold text-brand-accent mb-2">12K+</div>
                    <div className="text-white/80">Newsletter Subscribers</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold text-brand-accent mb-2">4.8</div>
                    <div className="text-white/80">Average Rating</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Featured Resources */}
            <FeaturedSection onResourceClick={handleResourceClick} />

            {/* Interactive Tools */}
            <InteractiveToolsSection onToolLaunch={handleToolLaunch} />

            {/* Search and Filter Section */}
            <section className="mb-12">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-text-primary mb-4">
                  Explore All Resources
                </h2>
                <p className="text-lg text-text-secondary max-w-3xl mx-auto">
                  Find exactly what you need with our comprehensive resource library
                </p>
              </div>

              {/* Search Bar */}
              <div className="max-w-2xl mx-auto mb-8">
                <div className="relative">
                  <Input
                    type="search"
                    placeholder="Search resources, topics, or content types..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="pl-12 pr-4 py-3 text-lg"
                  />
                  <Icon 
                    name="Search" 
                    size={20} 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary" 
                  />
                </div>
              </div>

              {/* Controls */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 space-y-4 lg:space-y-0">
                <div className="flex items-center space-x-4">
                  <span className="text-text-secondary">
                    {filteredResources?.length} resources found
                  </span>
                  {(Object.values(filters)?.some(arr => arr?.length > 0) || searchQuery) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      iconPosition="left"
                      onClick={handleClearFilters}
                    >
                      Clear All
                    </Button>
                  )}
                </div>

                <div className="flex items-center space-x-4">
                  {/* Sort Dropdown */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e?.target?.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                  >
                    <option value="newest">Newest First</option>
                    <option value="popular">Most Popular</option>
                    <option value="title">Alphabetical</option>
                  </select>

                  {/* View Mode Toggle */}
                  <div className="flex border border-gray-300 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 ${viewMode === 'grid' ? 'bg-brand-primary text-white' : 'bg-white text-text-secondary hover:text-text-primary'}`}
                    >
                      <Icon name="Grid3X3" size={16} />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 ${viewMode === 'list' ? 'bg-brand-primary text-white' : 'bg-white text-text-secondary hover:text-text-primary'}`}
                    >
                      <Icon name="List" size={16} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="grid lg:grid-cols-4 gap-8">
                {/* Filter Sidebar */}
                <div className="lg:col-span-1">
                  <FilterPanel
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    isOpen={isFilterOpen}
                    onToggle={() => setIsFilterOpen(!isFilterOpen)}
                  />
                </div>

                {/* Resources Grid */}
                <div className="lg:col-span-3">
                  {filteredResources?.length > 0 ? (
                    <div className={`grid gap-6 ${
                      viewMode === 'grid' ?'md:grid-cols-2 xl:grid-cols-3' :'grid-cols-1'
                    }`}>
                      {filteredResources?.map((resource) => (
                        <ResourceCard
                          key={resource?.id}
                          resource={resource}
                          onDownload={handleResourceDownload}
                          onView={handleResourceView}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <Icon name="Search" size={48} className="mx-auto text-text-secondary mb-4" />
                      <h3 className="text-xl font-semibold text-text-primary mb-2">
                        No resources found
                      </h3>
                      <p className="text-text-secondary mb-6">
                        Try adjusting your search terms or filters to find what you're looking for.
                      </p>
                      <Button
                        variant="outline"
                        onClick={handleClearFilters}
                        iconName="RotateCcw"
                        iconPosition="left"
                      >
                        Clear Filters
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Webinar Section */}
            <WebinarSection />

            {/* Newsletter Section */}
            <NewsletterSection />

            {/* CTA Section */}
            <section className="bg-white rounded-2xl shadow-executive border border-gray-200 p-8 lg:p-12 text-center">
              <h2 className="text-3xl font-bold text-text-primary mb-4">
                Ready to Start Your AI Independence Journey?
              </h2>
              <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
                Get personalized guidance from our AI independence experts and accelerate your transformation
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  variant="default"
                  className="bg-brand-primary hover:bg-brand-primary/90"
                  iconName="Calendar"
                  iconPosition="right"
                >
                  Schedule Consultation
                </Button>
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="right"
                >
                  Download AI Core Blueprint
                </Button>
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-text-primary text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div className="text-xl font-bold">AI Clean Core</div>
              </div>
              <p className="text-white/80 mb-6">
                Empowering organizations to achieve AI independence through open-source excellence
              </p>
              <div className="text-sm text-white/60">
                © {new Date()?.getFullYear()} AI Clean Core. All rights reserved.
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default ResourcesHub;